// src/components/MainLayout.js
import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth'; 
import { auth } from '../firebaseConfig'; 
import { useTranslation, Trans } from 'react-i18next';

// --- IMPORT MUI ---
import {
  Box, AppBar, Toolbar, IconButton, Drawer, Typography,
  List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  CssBaseline, Tooltip, Divider, ListSubheader, 
  Collapse,
  Chip,
  Select,
  MenuItem,
  FormControl,
  Alert
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import DashboardIcon from '@mui/icons-material/Dashboard';
import GroupsIcon from '@mui/icons-material/Groups';
import EditRoadIcon from '@mui/icons-material/EditRoad';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import CategoryIcon from '@mui/icons-material/Category';
import BusinessIcon from '@mui/icons-material/Business';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SportsMotorsportsIcon from '@mui/icons-material/SportsMotorsports';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import DescriptionIcon from '@mui/icons-material/Description'; 
import HomeRepairServiceIcon from '@mui/icons-material/HomeRepairService';
import WifiIcon from '@mui/icons-material/Wifi';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import ReceiptIcon from '@mui/icons-material/Receipt';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SpeedIcon from '@mui/icons-material/Speed';
import Brightness4Icon from '@mui/icons-material/Brightness4'; 
import Brightness7Icon from '@mui/icons-material/Brightness7'; 
import WarningIcon from '@mui/icons-material/Warning';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import TireRepairIcon from '@mui/icons-material/TireRepair';
// --- FINE IMPORT MUI ---

import WhatsNewDialog from './WhatsNewDialog';
import TrialEndingDialog from './TrialEndingDialog';


const drawerWidth = 260; 

// --- Definizioni Menu (Invariate) ---
const dashboardItem = { 
  textKey: 'menu.dashboard', 
  icon: <DashboardIcon />, 
  path: '/', 
  roles: ['admin', 'Engineer', 'Magazziniere', 'Mechanic', 'Guest'] 
};
const trackItems = [
  { textKey: 'menu.garage', icon: <DirectionsCarIcon />, path: '/garage', roles: ['admin', 'Engineer', 'Mechanic'] },
  { textKey: 'menu.events', icon: <CalendarMonthIcon />, path: '/events-calendar', roles: ['admin', 'Engineer', 'Mechanic'] },
  { textKey: 'menu.checklists', icon: <PlaylistAddCheckIcon />, path: '/checklist-manager', roles: ['admin', 'Engineer', 'Mechanic'] },
];
const workshopItem = { 
  textKey: 'menu.workshop', 
  icon: <HomeRepairServiceIcon />, 
  path: '/workshop', 
  roles: ['admin', 'Engineer', 'Mechanic'] 
};
const magazzinoSection = {
  textKey: 'menu.magazzino',
  icon: <WarehouseIcon />,
  roles: ['admin', 'Engineer', 'Magazziniere'], 
  subItems: [
    { textKey: 'menu.inventory', icon: <WarehouseIcon />, path: '/inventory', roles: ['admin', 'Engineer', 'Magazziniere'] },
    { textKey: 'menu.orders', icon: <ShoppingCartIcon />, path: '/order-manager', roles: ['admin', 'Engineer', 'Magazziniere'] },
    { textKey: 'menu.suppliers', icon: <BusinessIcon />, path: '/supplier-manager', roles: ['admin', 'Engineer', 'Magazziniere'] },
    { textKey: 'menu.categories', icon: <CategoryIcon />, path: '/category-manager', roles: ['admin', 'Engineer', 'Magazziniere'] }, 
  ]
};
const billingItem = {
  textKey: 'menu.billing',
  icon: <ReceiptIcon />,
  path: '/billing',
  roles: ['admin', 'Engineer']
};
const teamSection = { 
  textKey: 'menu.team', 
  icon: <GroupsIcon />, 
  roles: ['admin', 'Engineer', 'Mechanic'], 
  subItems: [
    { textKey: 'menu.team_management', icon: <GroupsIcon />, path: '/team-management', roles: ['admin', 'Engineer'] },
    { textKey: 'menu.driver_management', icon: <SportsMotorsportsIcon />, path: '/driver-management', roles: ['admin', 'Engineer', 'Mechanic'] },
    { textKey: 'menu.track_management', icon: <EditRoadIcon />, path: '/track-management', roles: ['admin', 'Engineer'] },
    { textKey: 'menu.setup_templates', icon: <DescriptionIcon />, path: '/setup-templates', roles: ['admin', 'Engineer'] },
  ]
};
const raceEngineerItem = {
  textKey: 'menu.race_engineer',
  icon: <SpeedIcon />,
  path: '/race-engineer',
  roles: ['admin', 'Engineer', 'Driver'],
};
const tiresItem = {
  textKey: 'menu.tires',
  icon: <TireRepairIcon />,
  path: '/tires',
  roles: ['admin', 'Engineer', 'Mechanic'],
};
// --- FINE DEFINIZIONE MENÙ ---

// --- COMPONENTE PER LA PROVA SCADUTA (Invariato) ---
function TrialExpiredLock({ t }) {
  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center', 
      height: 'calc(100vh - 150px)',
      textAlign: 'center',
      p: 3
    }}>
      <WarningIcon color="error" sx={{ fontSize: 80, mb: 2 }} />
      <Typography variant="h3" gutterBottom>
        {t('trial_expired.title')}
      </Typography>
      <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mb: 3 }}>
        {t('trial_expired.subtitle')}
      </Typography>
      <Alert severity="info" sx={{ maxWidth: 600, textAlign: 'left' }}>
        <Trans t={t} i18nKey="trial_expired.alert">
          Per continuare... 
          <a href="mailto:info@motorsportsuite.com" style={{ color: 'inherit', fontWeight: 'bold' }}>
            scrivici a info@motorsportsuite.com
          </a>.
        </Trans>
      </Alert>
    </Box>
  );
}
// --- FINE COMPONENTE ---

// --- CALCOLO GIORNI RIMANENTI (Invariato) ---
const calculateRemainingDays = (trialEndsAt) => {
  if (!trialEndsAt) return 0;
  const now = new Date();
  const endDate = trialEndsAt.toDate();
  const diffTime = endDate.getTime() - now.getTime();
  const remainingDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return remainingDays > 0 ? remainingDays : 0;
};


// --- MAINLAYOUT (Aggiornato) ---
function MainLayout({ userRole, mode, toggleTheme, isTrialExpired, trialEndsAt, teamLogos }) {
  const { t, i18n } = useTranslation('main-layout');
  
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [teamOpen, setTeamOpen] = useState(false); 
  const [magazzinoOpen, setMagazzinoOpen] = useState(false); 
  const [isOnline, setIsOnline] = useState(window.navigator.onLine);

  const remainingDays = calculateRemainingDays(trialEndsAt);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  
  const handleDrawerToggle = () => { setMobileOpen(!mobileOpen); };
  const handleLogout = () => { signOut(auth).catch((error) => console.error("Logout Error:", error)); };

  const handleNavigate = (path) => {
    navigate(path);
    if (mobileOpen) { setMobileOpen(false); }
  };
  
  const handleLangChange = (event) => {
    i18n.changeLanguage(event.target.value);
  };

  const checkRole = (rolesArray) => {
    if (!userRole || !rolesArray) return false;
    const lowerCaseUserRole = userRole.toLowerCase();
    return rolesArray.some(role => role.toLowerCase() === lowerCaseUserRole);
  };
  
  const filterByRole = (items) => items.filter(item => checkRole(item.roles));
  const finalTrackItems = filterByRole(trackItems);
  const finalWorkshopItem = checkRole(workshopItem.roles) ? workshopItem : null;
  const finalBillingItem = checkRole(billingItem.roles) ? billingItem : null;
  const finalRaceEngineerItem = checkRole(raceEngineerItem.roles) ? raceEngineerItem : null;
  const finalTiresItem        = checkRole(tiresItem.roles) ? tiresItem : null;
  const finalMagazzinoSubItems = magazzinoSection.subItems.filter(
    subItem => checkRole(subItem.roles)
  );
  const finalTeamSubItems = teamSection.subItems.filter(
    subItem => checkRole(subItem.roles)
  );

  // --- drawerContent (Invariato) ---
  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ flexShrink: 0 }}>
        <Toolbar sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, minHeight: 147 }}>
          <Box
            component="img"
            src={'/MSlogo.png'} 
            alt="MotorsportSuite Logo"
            sx={{
              height: 119, 
              width: 'auto',
              maxWidth: '220px', 
              objectFit: 'contain'
            }}
          />
        </Toolbar>
        <Divider />
        {checkRole(['Guest']) && ( 
          <Box sx={{ p: 2, m: 2, backgroundColor: 'primary.main', color: 'primary.contrastText', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <VisibilityIcon />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{t('guest_mode.title')}</Typography>
              <Typography variant="body2">{t('guest_mode.subtitle')}</Typography>
            </Box>
          </Box>
        )}
      </Box>
      <Box sx={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <List>
          {checkRole(dashboardItem.roles) && (
              <ListItem disablePadding>
                <ListItemButton onClick={() => handleNavigate(dashboardItem.path)}>
                  <ListItemIcon sx={{ color: 'text.secondary', minWidth: 40 }}>{dashboardItem.icon}</ListItemIcon>
                  <ListItemText primary={t(dashboardItem.textKey)} />
                </ListItemButton>
              </ListItem>
          )}
          {finalTrackItems.length > 0 && (
            <>
              <ListSubheader component="div" sx={{ backgroundColor: 'background.default' }}>
                {t('menu.track')}
              </ListSubheader>
              {finalTrackItems.map((item) => (
                <ListItem key={item.textKey} disablePadding>
                  <ListItemButton onClick={() => handleNavigate(item.path)}>
                    <ListItemIcon sx={{ color: 'text.secondary', minWidth: 40 }}>{item.icon}</ListItemIcon>
                    <ListItemText primary={t(item.textKey)} />
                  </ListItemButton>
                </ListItem>
              ))}
              {finalRaceEngineerItem && (
                <ListItem key="race-engineer" disablePadding>
                  <ListItemButton onClick={() => handleNavigate(finalRaceEngineerItem.path)}>
                    <ListItemIcon sx={{ color: 'text.secondary', minWidth: 40 }}>{finalRaceEngineerItem.icon}</ListItemIcon>
                    <ListItemText primary={t(finalRaceEngineerItem.textKey, 'Race Engineer AI')} />
                  </ListItemButton>
                </ListItem>
              )}
              {finalTiresItem && (
                <ListItem key="tires" disablePadding>
                  <ListItemButton onClick={() => handleNavigate(finalTiresItem.path)}>
                    <ListItemIcon sx={{ color: 'text.secondary', minWidth: 40 }}>{finalTiresItem.icon}</ListItemIcon>
                    <ListItemText primary={t(finalTiresItem.textKey, 'Gomme')} />
                  </ListItemButton>
                </ListItem>
              )}
            </>
          )}
          {(finalWorkshopItem || finalBillingItem || finalMagazzinoSubItems.length > 0) && (
            <>
              <Divider sx={{ my: 1 }} />
              <ListSubheader component="div" sx={{ backgroundColor: 'background.default' }}>
                {t('menu.factory')}
              </ListSubheader>
              {finalWorkshopItem && (
                <ListItem key={finalWorkshopItem.textKey} disablePadding>
                  <ListItemButton onClick={() => handleNavigate(finalWorkshopItem.path)}>
                    <ListItemIcon sx={{ color: 'text.secondary', minWidth: 40 }}>{finalWorkshopItem.icon}</ListItemIcon>
                    <ListItemText primary={t(finalWorkshopItem.textKey)} />
                  </ListItemButton>
                </ListItem>
              )}
              {finalBillingItem && (
                <ListItem key={finalBillingItem.textKey} disablePadding>
                  <ListItemButton onClick={() => handleNavigate(finalBillingItem.path)}>
                    <ListItemIcon sx={{ color: 'text.secondary', minWidth: 40 }}>
                      {finalBillingItem.icon}
                    </ListItemIcon>
                    <ListItemText primary={t(finalBillingItem.textKey)} />
                  </ListItemButton>
                </ListItem>
              )}
              {finalMagazzinoSubItems.length > 0 && (
                <>
                  <ListItemButton onClick={() => setMagazzinoOpen(!magazzinoOpen)} sx={{ mt: 0.5 }}>
                    <ListItemIcon sx={{ color: 'text.secondary', minWidth: 40 }}>
                      {magazzinoSection.icon}
                    </ListItemIcon>
                    <ListItemText primary={t(magazzinoSection.textKey)} />
                    {magazzinoOpen ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                  <Collapse in={magazzinoOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {finalMagazzinoSubItems.map((item) => (
                          <ListItemButton key={item.textKey} sx={{ pl: 4 }} onClick={() => handleNavigate(item.path)}>
                            <ListItemIcon sx={{ color: 'text.secondary', minWidth: 40 }}>
                              {item.icon}
                            </ListItemIcon>
                            <ListItemText primary={t(item.textKey)} />
                          </ListItemButton>
                      ))}
                    </List>
                  </Collapse>
                </>
              )}
            </>
          )}
          {finalTeamSubItems.length > 0 && (
            <>
              {!finalWorkshopItem && !finalBillingItem && finalMagazzinoSubItems.length === 0 && <Divider sx={{ my: 1 }} />}
              <ListItemButton onClick={() => setTeamOpen(!teamOpen)} sx={{ mt: 1 }}>
                <ListItemIcon sx={{ color: 'text.secondary', minWidth: 40 }}>
                  {teamSection.icon}
                </ListItemIcon>
                <ListItemText primary={t(teamSection.textKey)} />
                {teamOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
              <Collapse in={teamOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {finalTeamSubItems.map((item) => (
                      <ListItemButton key={item.textKey} sx={{ pl: 4 }} onClick={() => handleNavigate(item.path)}>
                        <ListItemIcon sx={{ color: 'text.secondary', minWidth: 40 }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText primary={t(item.textKey)} />
                      </ListItemButton>
                  ))}
                </List> 
              </Collapse>
            </>
          )}
        </List>
        <Box sx={{ flexGrow: 1 }} />
        <Divider />
        <List dense>
          <ListItem disablePadding>
            <ListItemButton
              component="a"
              href={i18n.language?.startsWith('en') ? '/docs/user-manual-en.html' : '/docs/manuale-utente.html'}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: 'text.secondary' }}
            >
              <ListItemIcon sx={{ color: 'text.secondary', minWidth: 40 }}>
                <MenuBookIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={i18n.language?.startsWith('en') ? 'User Manual' : 'Manuale Utente'}
                primaryTypographyProps={{ fontSize: 13 }}
              />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }} className="no-print">
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          backgroundColor: 'background.paper',
          color: 'text.primary',
          borderBottom: '1px solid',
          borderColor: 'divider',
          boxShadow: 'none' 
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }} 
          >
            <MenuIcon />
          </IconButton>
          
          <Box sx={{ flexGrow: 1 }} /> 

          {/* --- MODIFICA: Contatore Trial nascosto su mobile (xs) --- */}
          {!isTrialExpired && trialEndsAt && (
            <Box sx={{ display: { xs: 'none', sm: 'block' }, mr: 1.5 }}>
              <Tooltip title={t('trial.tooltip')}>
                <Chip
                  label={t('trial.days_remaining', { count: remainingDays })}
                  color={remainingDays <= 7 ? "error" : "success"}
                  variant="outlined"
                  size="small"
                  sx={{ fontWeight: 'bold' }}
                />
              </Tooltip>
            </Box>
          )}
          {/* --- FINE MODIFICA --- */}

          <Tooltip title={t('toggle_theme')}>
            <IconButton sx={{ mr: 1 }} onClick={toggleTheme} color="inherit">
              {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
          </Tooltip>

          <FormControl variant="standard" size="small" sx={{ minWidth: 80, mr: 1.5 }}>
            <Select
              value={i18n.language.includes('it') ? 'it' : 'en'}
              onChange={handleLangChange}
              disableUnderline
              sx={{
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                  paddingRight: '24px !important',
                },
                '& .MuiSelect-icon': {
                  color: 'text.secondary',
                  right: 0
                },
              }}
            >
              <MenuItem value="en">
                <span role="img" aria-label="English" style={{ marginRight: 8, fontSize: '1.2rem' }}>🇬🇧</span> EN
              </MenuItem>
              <MenuItem value="it">
                <span role="img" aria-label="Italiano" style={{ marginRight: 8, fontSize: '1.2rem' }}>🇮🇹</span> IT
              </MenuItem>
            </Select>
          </FormControl>

          <Chip
            icon={isOnline ? <WifiIcon /> : <WifiOffIcon />}
            label={isOnline ? t('connection.online') : t('connection.offline')} 
            color={isOnline ? 'success' : 'error'}
            variant="outlined"
            size="small"
            sx={{ mr: 1.5, fontWeight: 'bold' }}
          />

          <Tooltip title={t('logout')}>
            <IconButton color="inherit" onClick={handleLogout}> <LogoutIcon /> </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>
      
      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        <Drawer variant="temporary" open={mobileOpen} onClose={handleDrawerToggle} ModalProps={{ keepMounted: true }} sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, backgroundColor: 'background.paper', height: '100%' }, }}>
          {drawerContent}
        </Drawer>
        <Drawer variant="permanent" sx={{ display: { xs: 'none', md: 'block' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, backgroundColor: 'background.default', borderRight: '1px solid', borderColor: 'divider', height: '100%' }, }} open >
          {drawerContent}
        </Drawer>
      </Box>
      
      <Box component="main" sx={{ flexGrow: 1, p: 3, width: { md: `calc(100% - ${drawerWidth}px)` }, minHeight: '100vh', backgroundColor: 'background.default' }} >
        <Toolbar /> 
        
        {isTrialExpired ? (
          <TrialExpiredLock t={t} />
        ) : (
          <Outlet />
        )}

      </Box>

      {/* Popup novità versione — esce una volta sola finché la versione non cambia */}
      {!isTrialExpired && <WhatsNewDialog userId={auth.currentUser?.uid} />}
      {!isTrialExpired && <TrialEndingDialog trialEndsAt={trialEndsAt} />}
    </Box>
  );
}

export default MainLayout;